import React, { useState } from 'react';
import axios, { type AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, MoreHorizontal, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { RelativeTime } from '@/components/RelativeTime';
import { config } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { useTeams, type Team, formatTeamName } from '../../hooks/useTeams';
import { useUsers } from '../../hooks/useUsers';
import { isOwnerTransferRequired, useTransferOwnership } from '../../hooks/useTransferOwnership';
import { useTeamMembers } from '../../hooks/useTeamMembers';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface CreateTeamRequest {
  name: string;
  description?: string;
}

interface UpdateTeamRequest {
  name?: string;
  description?: string;
}

interface TeamRoleAssignment {
  contextId: string;
  contextName: string;
  roleId: string;
  roleName: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

export default function Teams() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isCreateModalActive, setIsCreateModalActive] = useState(false);
  const [isEditModalActive, setIsEditModalActive] = useState(false);
  const [isDetailModalActive, setIsDetailModalActive] = useState(false);
  const [isDeleteConfirmActive, setIsDeleteConfirmActive] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');

  const [editFormName, setEditFormName] = useState('');
  const [editFormDescription, setEditFormDescription] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  const [submitError, setSubmitError] = useState<string | null>(null);
  // The removal the API refused until ownership moves, held so the dialog can
  // retry the exact same request once a successor is chosen.
  const [pendingOwnerRemoval, setPendingOwnerRemoval] = useState<{
    teamId: string;
    userId: string;
  } | null>(null);
  const [successorId, setSuccessorId] = useState('');

  const backendApi = config.backendApi;

  const { data: teams, isLoading, isError, error } = useTeams(organizationId);
  const { data: users } = useUsers(organizationId);
  const transferOwnership = useTransferOwnership();

  const { data: teamMembers } = useTeamMembers(selectedTeam?.id);

  const { data: teamRoles } = useQuery<TeamRoleAssignment[]>({
    queryKey: ['team-roles', selectedTeam?.id, backendApi, selectedTeam],
    queryFn: async () => {
      if (!selectedTeam) return [];
      const response = await axios.get<{ assignments: TeamRoleAssignment[] }>(
        `${backendApi}/api/teams/${selectedTeam.id}/roles`,
        { withCredentials: true },
      );
      return response.data.assignments;
    },
    enabled: !!selectedTeam,
  });

  const createTeamMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    CreateTeamRequest
  >({
    mutationFn: async (newTeam) => {
      await axios.post(`${backendApi}/api/teams`, newTeam, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsCreateModalActive(false);
      resetCreateForm();
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to create team.',
      );
    },
  });

  const updateTeamMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { id: string; data: UpdateTeamRequest }
  >({
    mutationFn: async ({ id, data }) => {
      await axios.put(`${backendApi}/api/teams/${id}`, data, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsEditModalActive(false);
      setEditingTeam(null);
      setEditFormName('');
      setEditFormDescription('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to update team.',
      );
    },
  });

  const deleteTeamMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    string
  >({
    mutationFn: async (id) => {
      await axios.delete(`${backendApi}/api/teams/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to delete team.',
      );
    },
  });

  const addMemberMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { teamId: string; userId: string }
  >({
    mutationFn: async ({ teamId, userId }) => {
      await axios.post(
        `${backendApi}/api/teams/${teamId}/members`,
        { userId },
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-members', selectedTeam?.id] });
      setSelectedUserId('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ||
          err.response?.data.error ||
          'Failed to add member to team.',
      );
    },
  });

  const removeMemberMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { teamId: string; userId: string }
  >({
    mutationFn: async ({ teamId, userId }) => {
      await axios.delete(`${backendApi}/api/teams/${teamId}/members/${userId}`, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-members', selectedTeam?.id] });
      setSubmitError(null);
    },
    onError: (err, variables) => {
      // Not a failure so much as a prerequisite: the org's owner cannot be
      // detached until someone else owns it. Offer the handover rather than
      // reporting a dead end.
      if (isOwnerTransferRequired(err)) {
        setPendingOwnerRemoval(variables);
        setSuccessorId('');
        setSubmitError(null);
        return;
      }
      setSubmitError(
        err.response?.data.description ||
          err.response?.data.error ||
          'Failed to remove member from team.',
      );
    },
  });

  const resetCreateForm = () => {
    setFormName('');
    setFormDescription('');
  };

  const handleCreateTeam = (e: React.SubmitEvent) => {
    e.preventDefault();
    createTeamMutation.mutate({ name: formName, description: formDescription || undefined });
  };

  const handleUpdateTeam = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (editingTeam) {
      updateTeamMutation.mutate({
        id: editingTeam.id,
        data: { name: editFormName, description: editFormDescription || undefined },
      });
    }
  };

  const handleDeleteTeam = (team: Team) => {
    if (team.isImmutable) {
      setSubmitError(
        'Cannot delete SSO-synchronized teams. They must be removed from the SSO provider.',
      );
      return;
    }
    setTeamToDelete(team);
    setIsDeleteConfirmActive(true);
  };

  const confirmDeleteTeam = () => {
    if (teamToDelete) {
      deleteTeamMutation.mutate(teamToDelete.id);
      setIsDeleteConfirmActive(false);
      setTeamToDelete(null);
    }
  };

  const cancelDeleteTeam = () => {
    setIsDeleteConfirmActive(false);
    setTeamToDelete(null);
  };

  const openCreateModal = () => {
    setIsCreateModalActive(true);
    resetCreateForm();
    setSubmitError(null);
  };

  const openEditModal = (team: Team) => {
    if (team.isImmutable) {
      setSubmitError(
        'Cannot edit SSO-synchronized teams. They are managed by your SSO provider.',
      );
      return;
    }
    setEditingTeam(team);
    setEditFormName(team.name);
    setEditFormDescription(team.description || '');
    setIsEditModalActive(true);
    setSubmitError(null);
  };

  const openDetailModal = (team: Team) => {
    setSelectedTeam(team);
    setSelectedUserId('');
    // The dialog shows submitError now, so open it clean rather than greeting
    // the user with a failure left over from something else on the page.
    setSubmitError(null);
    setIsDetailModalActive(true);
    setSubmitError(null);
  };

  const handleAddMember = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (selectedTeam && selectedUserId) {
      addMemberMutation.mutate({ teamId: selectedTeam.id, userId: selectedUserId });
    }
  };

  const handleRemoveMember = (userId: string) => {
    if (selectedTeam) {
      removeMemberMutation.mutate({ teamId: selectedTeam.id, userId });
    }
  };

  /**
   * Hands the organization to the chosen member, then retries the removal the
   * API refused. Sequential on purpose: the retry is only valid once ownership
   * has actually moved, so a failed transfer must leave the membership alone.
   */
  const handleTransferAndRemove = async () => {
    if (!pendingOwnerRemoval || !organizationId || !successorId) return;
    try {
      await transferOwnership.mutateAsync({
        organizationId,
        newOwnerUserId: successorId,
      });
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>;
      setSubmitError(
        axiosError.response?.data.description ||
          axiosError.response?.data.error ||
          'Failed to transfer ownership.',
      );
      return;
    }
    const retry = pendingOwnerRemoval;
    setPendingOwnerRemoval(null);
    setSuccessorId('');
    removeMemberMutation.mutate(retry);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Teams" />
        <p className="text-sm text-muted-foreground">Loading teams…</p>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <PageHeader title="Teams" />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading teams: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const teamColumns: readonly ResponsiveTableColumn<Team>[] = [
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (team) => <span className="font-medium">{formatTeamName(team)}</span>,
    },
    {
      key: 'description',
      label: 'Description',
      render: (team) => (
        <span className="text-xs text-muted-foreground">
          {team.description || 'No description'}
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (team) => (
        <Badge
          className={
            team.isImmutable
              ? 'bg-sev-3/15 text-foreground border-0 font-normal'
              : 'bg-sev-4/15 text-sev-4 border-0 font-normal'
          }
        >
          {team.isImmutable ? 'SSO group' : 'Custom team'}
        </Badge>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (team) =>
        team.createdAt ? (
          <span className="text-xs text-muted-foreground">
            <RelativeTime value={team.createdAt} />
          </span>
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Teams"
        subtitle="Manage teams and assign roles for permission control"
        primary={
          <Button onClick={openCreateModal} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Create team
          </Button>
        }
      />

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      <ResponsiveTable<Team>
        columns={teamColumns}
        rows={teams ?? []}
        rowKey={(t) => t.id}
        isLoading={isLoading}
        rowActions={(team) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${formatTeamName(team)}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => { openDetailModal(team); }}
                className="gap-2"
              >
                <Users className="w-4 h-4" />
                Manage members
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { openEditModal(team); }}
                disabled={team.isImmutable}
                className="gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { handleDeleteTeam(team); }}
                disabled={team.isImmutable}
                className="gap-2 text-sev-1 focus:text-sev-1"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={<EmptyState variant="no-data" title="No teams found" />}
      />

      <AppSheet
        open={isCreateModalActive}
        onOpenChange={(open) => { if (!open) setIsCreateModalActive(false); }}
        title="Create new team"
        size="md"
      >
        <AppSheet.Body>
          <form id="create-team-form" onSubmit={handleCreateTeam} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ct-name">Name</Label>
              <Input
                id="ct-name"
                type="text"
                placeholder="Team name"
                value={formName}
                onChange={(e) => { setFormName(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ct-desc">Description (optional)</Label>
              <Textarea
                id="ct-desc"
                placeholder="Team description or purpose"
                value={formDescription}
                onChange={(e) => { setFormDescription(e.target.value); }}
                rows={3}
              />
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsCreateModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-team-form"
            disabled={createTeamMutation.isPending}
          >
            {createTeamMutation.isPending ? 'Saving…' : 'Create team'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isEditModalActive}
        onOpenChange={(open) => { if (!open) setIsEditModalActive(false); }}
        title={`Edit team: ${editingTeam ? formatTeamName(editingTeam) : ''}`}
        size="md"
      >
        <AppSheet.Body>
          <form id="edit-team-form" onSubmit={handleUpdateTeam} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="et-name">Name</Label>
              <Input
                id="et-name"
                type="text"
                placeholder="Team name"
                value={editFormName}
                onChange={(e) => { setEditFormName(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-desc">Description (optional)</Label>
              <Textarea
                id="et-desc"
                placeholder="Team description or purpose"
                value={editFormDescription}
                onChange={(e) => { setEditFormDescription(e.target.value); }}
                rows={3}
              />
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsEditModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-team-form"
            disabled={updateTeamMutation.isPending}
          >
            {updateTeamMutation.isPending ? 'Saving…' : 'Update team'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <Dialog
        open={isDetailModalActive}
        onOpenChange={(open) => {
          if (!open) {
            setIsDetailModalActive(false);
            // Otherwise the failure just handled inside the dialog reappears on
            // the page underneath it.
            setSubmitError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Team details: {selectedTeam ? formatTeamName(selectedTeam) : ''}
            </DialogTitle>
          </DialogHeader>
          {selectedTeam ? (
            <div className="space-y-4 text-sm">
              <div className="space-y-2">
                <p>
                  <strong>Description:</strong>{' '}
                  {selectedTeam.description || 'No description'}
                </p>
                <p className="flex items-center gap-2">
                  <strong>Type:</strong>
                  <Badge
                    className={
                      selectedTeam.isImmutable
                        ? 'bg-sev-3/15 text-foreground border-0 font-normal'
                        : 'bg-sev-4/15 text-sev-4 border-0 font-normal'
                    }
                  >
                    {selectedTeam.isImmutable ? 'SSO group' : 'Custom team'}
                  </Badge>
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-sm font-semibold mb-2">Team members</h3>
                {/* Adding and removing members happen in here, so their failures
                    have to surface in here too — the page-level alert sits behind
                    the modal overlay, dimmed and easy to miss entirely. */}
                {submitError ? (
                  <Alert variant="destructive" className="mb-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                ) : null}
                <div className="rounded-md border border-border-subtle bg-muted/30 p-3 mb-3">
                  <p className="text-sm font-semibold mb-2">Add member</p>
                  <form
                    onSubmit={handleAddMember}
                    className="flex flex-col md:flex-row gap-2"
                  >
                    <Select
                      value={selectedUserId}
                      onValueChange={setSelectedUserId}
                      required
                      disabled={selectedTeam.isImmutable}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a user to add…" />
                      </SelectTrigger>
                      <SelectContent>
                        {users
                          ?.filter(
                            (u) => !teamMembers?.some((m) => m.id === u.id),
                          )
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.firstName} {u.lastName} ({u.email})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="submit"
                      disabled={
                        addMemberMutation.isPending || selectedTeam.isImmutable
                      }
                    >
                      {addMemberMutation.isPending ? 'Adding…' : 'Add member'}
                    </Button>
                  </form>
                  {selectedTeam.isImmutable ? (
                    <p className="text-xs text-sev-3 mt-2">
                      Cannot modify SSO team members. They are managed by your SSO provider.
                    </p>
                  ) : null}
                </div>

                {teamMembers && teamMembers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border-subtle">
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2 font-medium">Name</th>
                          <th className="px-3 py-2 font-medium">Email</th>
                          <th className="px-3 py-2 font-medium w-24" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle">
                        {teamMembers.map((member) => (
                          <tr key={member.id}>
                            <td className="px-3 py-2">
                              {member.firstName} {member.lastName}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {member.email}
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-sev-1 hover:text-sev-1"
                                onClick={() => { handleRemoveMember(member.id); }}
                                disabled={
                                  removeMemberMutation.isPending ||
                                  selectedTeam.isImmutable
                                }
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No members in this team yet.
                  </p>
                )}
              </div>

              <Separator />

              <div>
                <h3 className="text-sm font-semibold mb-2">Role assignments</h3>
                {teamRoles && teamRoles.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border-subtle">
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2 font-medium">Context</th>
                          <th className="px-3 py-2 font-medium">Role</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle">
                        {teamRoles.map((assignment, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2">{assignment.contextName}</td>
                            <td className="px-3 py-2">
                              <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
                                {assignment.roleName}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No role assignments for this team.
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingOwnerRemoval !== null}
        onOpenChange={(next) => {
          if (!next && !transferOwnership.isPending && !removeMemberMutation.isPending) {
            setPendingOwnerRemoval(null);
            setSuccessorId('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer ownership first</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This is the last team in the organization for its owner. Removing them
            would leave them owning an organization they can&apos;t open, so choose
            who owns it instead.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="tt-successor">New owner</Label>
            <Select value={successorId} onValueChange={setSuccessorId}>
              <SelectTrigger id="tt-successor">
                <SelectValue placeholder="Select a member…" />
              </SelectTrigger>
              <SelectContent>
                {(users ?? [])
                  .filter((candidate) => candidate.id !== pendingOwnerRemoval?.userId)
                  .map((candidate) => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {`${candidate.firstName} ${candidate.lastName}`.trim() || candidate.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              They must already be a member here, and must not already own another
              organization.
            </p>
          </div>

          {submitError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setPendingOwnerRemoval(null);
                setSuccessorId('');
              }}
              disabled={transferOwnership.isPending || removeMemberMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => { void handleTransferAndRemove(); }}
              disabled={
                !successorId || transferOwnership.isPending || removeMemberMutation.isPending
              }
            >
              {transferOwnership.isPending || removeMemberMutation.isPending
                ? 'Transferring…'
                : 'Transfer and remove'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isActive={isDeleteConfirmActive}
        title="Delete team"
        message={`Are you sure you want to delete the team "${teamToDelete ? formatTeamName(teamToDelete) : 'Unknown'}"? This action cannot be undone and will affect all users and permissions associated with this team.`}
        confirmText="Delete team"
        cancelText="Cancel"
        onConfirm={confirmDeleteTeam}
        onCancel={cancelDeleteTeam}
        isLoading={deleteTeamMutation.isPending}
        isDanger={true}
      />
    </PageContainer>
  );
}
